/**
 * @fileoverview Catalog tab content for existing SKU management workflow.
 * @module features/products/components/CatalogTab
 */

import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Package, PackagePlus, Plus } from 'lucide-react'

import { Skeleton } from '@/components/ui/skeleton'
import { Card, CardContent, CardFooter } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { type Product } from '@/lib/productService'
import { ProductCard } from './ProductCard'

/**
 * Ghost product card for empty state - dark silhouette version of ProductCard.
 */
function GhostProductCard(): ReactElement {
  return (
    <Card className="glass-card !p-0 overflow-hidden opacity-30 pointer-events-none">
      <div className="aspect-square w-full overflow-hidden bg-muted/20 relative border-b border-border/50">
        <div className="flex h-full items-center justify-center text-muted-foreground/20">
          <Package className="size-12" />
        </div>
      </div>
      <div className="bg-black/10">
        <CardContent className="p-4 pb-3 space-y-2">
          <div className="h-4 w-3/4 bg-muted-foreground/10 rounded" />
          <div className="h-3 w-1/2 bg-muted-foreground/10 rounded" />
        </CardContent>
        <div className="border-t border-border/40" />
        <CardFooter className="p-4 pt-3 flex items-center justify-between">
          <div className="h-5 w-16 bg-muted-foreground/10 rounded" />
          <div className="h-4 w-4 bg-muted-foreground/10 rounded" />
        </CardFooter>
      </div>
    </Card>
  )
}

/**
 * Add product card - first card in empty state grid with dropdown menu.
 */
function AddProductCard(): ReactElement {
  const { t } = useTranslation('features-products')
  const navigate = useNavigate()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Card className="glass-card !p-0 overflow-hidden cursor-pointer transition-all hover:border-primary/50 group">
          <div className="aspect-square w-full overflow-hidden bg-muted/20 relative border-b border-border/50">
            <div className="flex h-full items-center justify-center text-muted-foreground group-hover:text-foreground transition-colors">
              <PackagePlus className="size-12" />
            </div>
          </div>
          <div className="bg-black/10">
            <CardContent className="p-4 pb-3 space-y-2">
              <div className="h-4 text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                {t('actions.addProduct')}
              </div>
              <p className="text-[10px] text-muted-foreground/50 font-mono uppercase">sku-slug</p>
            </CardContent>
            <div className="border-t border-border/40" />
            <CardFooter className="p-4 pt-3">
              <span className="text-sm text-muted-foreground/50">0.00 zł</span>
            </CardFooter>
          </div>
        </Card>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="right" align="start" sideOffset={8}>
        <DropdownMenuItem onClick={() => navigate('/dashboard/management/import/baselinker')}>
          <Plus className="size-4" />
          {t('actions.importBaselinker')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/dashboard/management/import/shopify')}>
          <Plus className="size-4" />
          {t('actions.importShopify', 'Add from Shopify')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/dashboard/management/products/new')}>
          <Package className="size-4" />
          {t('create.actions.submit')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * Empty state component with add card and ghost cards.
 */
function CatalogEmptyState(): ReactElement {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-6 gap-y-8">
      <AddProductCard />
      {Array.from({ length: 9 }).map((_, i) => (
        <GhostProductCard key={i} />
      ))}
    </div>
  )
}

/**
 * Props for the catalog tab content.
 */
export interface CatalogTabProps {
  /** Optional sku id to highlight when navigating from Needs Review */
  highlightSkuId?: number
  /** List of products to display */
  products: Product[]
  /** Loading state */
  isLoading: boolean
}

/**
 * SKU catalog tab featuring grid layout.
 * @component
 */
export function CatalogTab({ highlightSkuId, products, isLoading }: CatalogTabProps): ReactElement {
  return (
    <div className="mx-auto max-w-screen-2xl space-y-6 px-4 pb-8 pt-4 lg:px-8">
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-6 gap-y-8">
           {Array.from({ length: 12 }).map((_, i) => (
             <div key={i} className="space-y-3 rounded-xl border p-4">
               <Skeleton className="aspect-square w-full rounded-lg" />
               <Skeleton className="h-4 w-3/4" />
               <Skeleton className="h-4 w-1/2" />
             </div>
           ))}
        </div>
      ) : products.length === 0 ? (
        <CatalogEmptyState />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-6 gap-y-8">
          {products.map(product => (
            <ProductCard
              key={product.id}
              product={product}
              highlighted={highlightSkuId ? product.variants?.some(v => v.id === highlightSkuId) : false}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default CatalogTab
